import { Component, OnInit, computed, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { AppStateService } from 'src/app/store/app-state.service';
import { FullScreenPreviewService } from 'src/app/utils/full-screen-preview/full-screen-preview.service';
import { ConfirmationComponent } from 'src/app/modals/confirmation/confirmation.component';
import {
  DashboardShopifyService,
  ShopifyLearningStatus,
} from './dashboard-shopify.service';
import { isDashboardMock } from './dashboard-shopify.mock';
import { NotificationService } from 'src/app/core/services/notification.service';

type ExploreCardKey = 'walkthrough' | 'onePager' | 'caseStudy' | 'demo';

interface ExploreCard {
  key: ExploreCardKey;
  title: string;
  subtitle: string;
  theme: string;
  icon: 'play' | 'doc' | 'search';
}

@Component({
  selector: 'app-dashboard-shopify',
  templateUrl: './dashboard-shopify.component.html',
  styleUrls: ['./dashboard-shopify.component.css'],
})
export class DashboardShopifyComponent implements OnInit {
  private appState = inject(AppStateService);
  private fullScreenPreviewService = inject(FullScreenPreviewService);
  private shopifyService = inject(DashboardShopifyService);
  private dialog = inject(MatDialog);
  private notification = inject(NotificationService);

  // `?dashboardMock=1` previews the LIVE view with dummy data.
  public readonly mockPreview = isDashboardMock();

  public learningMode = computed(
    () => !this.mockPreview && this.appState.learningMode(),
  );

  // Targets on the new nudge view get the winning experiments in the Nudge panel.
  public abTestingView = computed(() => this.appState.abTestingView());

  // Learning status shown in the hero banner. Populated from the backend once
  // the endpoint is ready (see loadLearningStatus). Defaults keep the view
  // meaningful in the meantime.
  public learningStatus: ShopifyLearningStatus = {
    label: 'Analyzing shopper behaviour',
    percentage: 12,
    estimatedTimeLeftLabel: '~1 day left',
    autoActivate: true,
  };

  // "Auto-activate when ready" toggle. Kept in sync with learningStatus.
  public autoActivate = computed(() => this.appState.autoActivate());

  // "Disable Behamics" toggle in the LIVE banner. true = Behamics is active
  // (nudges & signals running on the store).
  public behamicsDeactivated = computed(
    () => !this.mockPreview && this.appState.behamicsDeactivated(),
  );

  // Cards under "While we learn, get to know Behamics". Wired to the same
  // resources & mechanism the sidenav uses (video preview + PDF in new tab).
  private readonly allExploreCards: ExploreCard[] = [
    {
      key: 'walkthrough',
      title: 'Walkthrough',
      subtitle: 'Guided product tour',
      theme: 'walkthrough',
      icon: 'play',
    },
    {
      key: 'onePager',
      title: 'One Pager',
      subtitle: 'The essentials at a glance',
      theme: 'one-pager',
      icon: 'doc',
    },
    {
      key: 'caseStudy',
      title: 'Case Study',
      subtitle: 'Real store results',
      theme: 'case-study',
      icon: 'search',
    },
    {
      key: 'demo',
      title: 'Demo',
      subtitle: 'See it live in action',
      theme: 'demo',
      icon: 'play',
    },
  ];

  // Walkthrough and Demo are hidden for now on the A/B Testing view
  // (nudge-v2); those targets get the two documents only.
  public exploreCards = computed(() =>
    this.abTestingView()
      ? this.allExploreCards.filter(
          (card) => card.key !== 'walkthrough' && card.key !== 'demo',
        )
      : this.allExploreCards,
  );

  // Resource links for the explore cards. These mirror the Nudge product
  // resources referenced in utils/variables.ts (videoExplanationSrc &
  // organicPDFLink) and drive the same mechanism the sidenav uses.
  private readonly resources: Record<ExploreCardKey, string> = {
    walkthrough:
      'https://behamicsfront.blob.core.windows.net/clients-screenshots/behamicsWeb/dashboard_explanations/Nudge - Overview.mp4',
    demo: 'https://cdn.behamics.com/clients-screenshots/behamicsWeb/dashboard_explanations/Nudge Demo.mp4',
    onePager:
      'https://cdn.behamics.com/clients-screenshots/behamicsWeb/onboarding-cs-sales/Nudge/One Pager/Nudge One-Pager.pdf',
    caseStudy:
      'https://behamicsfront.blob.core.windows.net/clients-screenshots/behamicsWeb/onboarding-cs-sales/case_studies/Nudge_Case_Study.pdf',
  };

  ngOnInit(): void {
    this.loadLearningStatus();
  }

  /**
   * Loads the store's learning status.
   *
   * The endpoint is not available yet, so the wired-up call is commented out
   * and we fall back to the defaults above. When the backend ships, uncomment
   * the block below and delete the placeholder defaults if desired.
   */
  private loadLearningStatus(): void {
    // this.shopifyService.getLearningStatus().subscribe({
    //   next: (status) => {
    //     this.learningStatus = status;
    //     this.autoActivate = status.autoActivate;
    //     this.appState.learningMode.set(status.learningMode);
    //   },
    //   error: (err) => console.log(err),
    // });
  }

  public onExploreCard(key: ExploreCardKey): void {
    switch (key) {
      case 'walkthrough':
        this.openVideo(this.resources.walkthrough, 'Walkthrough');
        break;
      case 'demo':
        this.openVideo(this.resources.demo, 'Demo');
        break;
      case 'onePager':
        this.openPdf(this.resources.onePager);
        break;
      case 'caseStudy':
        this.openPdf(this.resources.caseStudy);
        break;
    }
  }

  public onAutoActivateChange(enabled: boolean): void {
    this.appState.autoActivate.set(enabled);

    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '430px',
      data: {
        header: !enabled ? 'Disable auto-activate' : 'Enable Auto-activate',
        content: !enabled
          ? 'Are you sure you want to disable auto-activate? '
          : 'Are you sure you want to enable auto-activate?',
        confirm_btn: !enabled ? 'Disable' : 'Enable',
        confirmButtonDangerStyle: !enabled,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result == 1) {
        this.shopifyService.setAutoActivate(enabled).subscribe({
          next: (res) => {
            this.notification.info(
              enabled ? 'Auto-activate enabled!' : 'Auto-activate disabled!',
            );
          },
          error: () => this.notification.error(),
        });
      } else {
        enabled = !enabled;
        this.appState.autoActivate.set(enabled);
      }
    });
  }

  public onBehamicsToggle(event: MatSlideToggleChange): void {
    const desired = event.checked;

    const disabling = !desired;
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '430px',
      data: {
        header: disabling ? 'Disable Behamics' : 'Enable Behamics',
        content: disabling
          ? 'Are you sure you want to disable Behamics? Nudges and signals will stop running on your store.'
          : 'Are you sure you want to enable Behamics? Nudges and signals will start running on your store again.',
        confirm_btn: disabling ? 'Disable' : 'Enable',
        confirmButtonDangerStyle: disabling,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result == 1) {
        this.appState.behamicsDeactivated.set(!desired);

        this.shopifyService.setBehamicsEnabled(!desired).subscribe({
          next: (res: any) => {
            this.notification.info(
              desired
                ? 'Enabled Behamics successfully!'
                : 'Disabled Behamics successfully!',
            );
          },
          error: (err) => {
            this.notification.error();
          },
        });
      } else {
        event.source.checked = !desired;
      }
    });
  }

  // Mirrors SidenavComponent.displayVideo — opens the shared full-screen preview.
  private openVideo(src: string, label: string): void {
    if (!src) return;

    this.fullScreenPreviewService.setContent({
      src,
      html: '',
      label,
      isHTML: false,
      showLabel: false,
      isVideo: true,
    });
    this.fullScreenPreviewService.openPreview();
  }

  // Mirrors SidenavComponent.openPdfClicked — opens the PDF in a new tab.
  private openPdf(src: string): void {
    this.fullScreenPreviewService.setContent({
      src: src,
      html: '',
      label: '',
      isHTML: false,
      showLabel: false,
      isPdf: true,
    });
    this.fullScreenPreviewService.openPreview();
  }
}
